<template>
  <div>
    <v-card class="mb-4">
      <v-card-text>
        <VehicleGPSHistoryMap
          :gps-data="gpsData"
          :data-by-event-id="dataByEventId"
          @update:dataByEventId="dataByEventId = $event"
        />
      </v-card-text>
    </v-card>

    <v-tabs v-model="currentTab">
      <v-tab key="gps">GPS History</v-tab>
      <v-tab key="trips">Trip Reports</v-tab>
      <v-tab key="events">Device Events</v-tab>
    </v-tabs>

    <v-tabs-items v-model="currentTab" touchless>
      <v-tab-item>
        <v-card class="mb-4">
          <v-card-text>
            <v-row class="mb-1">
              <v-col cols="12">
                <h2>GPS History</h2>
              </v-col>
            </v-row>

            <v-row class="mt-1 mb-1" style="align-items: center">
              <v-col cols="5">
                <v-text-field
                  id="gpsStartDateTime"
                  v-model="gpsStartDateTime"
                  label="Start datetime"
                  outlined
                  hide-details
                  dense
                  type="datetime-local"
                  :min="lastMonth()"
                  :max="today()"
                />
              </v-col>
              <v-col cols="5">
                <v-text-field
                  id="gpsEndDateTime"
                  v-model="gpsEndDateTime"
                  label="End datetime"
                  outlined
                  hide-details
                  dense
                  type="datetime-local"
                  :min="lastMonth()"
                  :max="today()"
                />
              </v-col>
              <v-col cols="2">
                <v-btn color="primary" style="min-width: 100%" @click="fetchGPSData">Go</v-btn>
              </v-col>
            </v-row>

            <div v-if="gpsProgress.total" class="progress">
              <div class="progress__message">
                <div v-if="gpsProgress.finished">Successfully fetched GPS positions!</div>
                <div v-else>
                  <div>{{ gpsProgress.current }} / {{ gpsProgress.total }}...</div>
                  <div v-if="dateTimes.gpsStartDateTime">
                    {{ dateTimes.gpsStartDateTime }} to {{ dateTimes.gpsEndDateTime }}...
                  </div>
                </div>
              </div>
              <v-progress-linear v-model="gpsProgress.percentage"></v-progress-linear>
            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <v-tab-item>
        <v-card>
          <v-card-text>
            <v-row class="mb-1">
              <v-col cols="12">
                <h2 class="mb-2">Trip History</h2>
              </v-col>
            </v-row>

            <v-row class="mt-1 mb-1" style="align-items: center">
              <v-col cols="5">
                <v-text-field
                  id="tripReportsStartDate"
                  v-model="tripReportsStartDate"
                  label="Start date"
                  outlined
                  hide-details
                  dense
                  type="date"
                />
              </v-col>
              <v-col cols="5">
                <v-text-field
                  id="tripReportsEndDate"
                  v-model="tripReportsEndDate"
                  label="End date"
                  outlined
                  hide-details
                  dense
                  type="date"
                />
              </v-col>
              <v-col cols="2">
                <v-btn color="primary" style="min-width: 100%" @click="fetchTripReportsData">Go</v-btn>
              </v-col>
            </v-row>

            <div v-if="tripReportsProgress.total" class="progress">
              <div class="progress__message">
                <div v-if="tripReportsProgress.finished">Successfully fetched trip reports</div>
                <div v-else>
                  <div>{{ tripReportsProgress.current }} / {{ tripReportsProgress.total }}...</div>
                  <div v-if="tripReportsStartDate">{{ tripReportsStartDate }} to {{ tripReportsEndDate }}...</div>
                </div>
              </div>
              <v-progress-linear v-model="tripReportsProgress.percentage"></v-progress-linear>
            </div>

            <v-row v-if="tripReports.length || tripReportsProgress.finished">
              <v-col cols="12">
                <h3 class="mb-2">Trip Reports</h3>

                <v-data-table
                  :headers="[
                    {
                      text: 'Trip ID',
                      value: 'trip_id',
                    },
                    {
                      text: 'Start',
                      value: 'start_datetime',
                    },
                    {
                      text: 'End',
                      value: 'end_datetime',
                    },
                    {
                      text: 'Stops Announced',
                      value: 'stops_announced',
                    },
                    {
                      text: 'View',
                    },
                  ]"
                  :items="tripReports"
                >
                  <template v-slot:item="{ item }">
                    <tr>
                      <td>{{ item.trip_id }}</td>
                      <td>{{ item.start_datetime }}</td>
                      <td>{{ item.end_datetime }}</td>
                      <td>
                        <span>{{ formatPercentage(item.stops_announced / item.stops_expected) }}</span>
                        <br /><small>{{ item.stops_announced }} / {{ item.stops_expected }}</small>
                      </td>
                      <td>
                        <v-btn icon small @click="() => showTripReportByEvent(item.id)">
                          <v-icon>
                            {{ icons.mdiMapSearch }}
                          </v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <v-tab-item>
        <v-card>
          <v-card-text>
            <v-row class="mb-1">
              <v-col cols="12">
                <h2 class="mb-2">Device Events</h2>
              </v-col>
            </v-row>

            <v-row class="mt-1 mb-1" style="align-items: center">
              <v-col cols="5">
                <v-text-field
                  id="eventsStartDate"
                  v-model="eventsStartDate"
                  label="Start date"
                  outlined
                  hide-details
                  dense
                  type="date"
                />
              </v-col>
              <v-col cols="5">
                <v-text-field
                  id="eventsEndDate"
                  v-model="eventsEndDate"
                  label="End date"
                  outlined
                  hide-details
                  dense
                  type="date"
                />
              </v-col>
              <v-col cols="2">
                <v-btn color="primary" style="min-width: 100%" @click="fetchEventsData">Go</v-btn>
              </v-col>
            </v-row>

            <div v-if="eventsProgress.total" class="progress">
              <div class="progress__message">
                <div v-if="eventsProgress.finished">Successfully fetched events</div>
                <div v-else>
                  <div>{{ eventsProgress.current }} / {{ eventsProgress.total }}...</div>
                  <div v-if="eventsStartDate">{{ eventsStartDate }} to {{ eventsEndDate }}...</div>
                </div>
              </div>
              <v-progress-linear v-model="eventsProgress.percentage"></v-progress-linear>
            </div>

            <v-row v-if="eventsData.length || eventsProgress.finished">
              <v-col cols="12">
                <v-data-table
                  :headers="[
                    {
                      text: 'Type',
                      value: 'type',
                      sortable: false,
                    },
                    {
                      text: 'Action',
                      value: 'action',
                      sortable: false,
                    },
                    {
                      text: 'Trip ID',
                      value: 'trip_id',
                      sortable: false,
                    },
                    {
                      text: 'Date/Time',
                      value: 'date_time',
                    },
                  ]"
                  :items="eventsData"
                >
                  <template v-slot:item="{ item }">
                    <tr>
                      <td>
                        <span v-if="item.type === 'off_route_message'">Off route message</span>
                        <span v-if="item.type === 'trip_assignment'">Trip assignment</span>
                      </td>
                      <td>{{ item.action || "null" }}</td>
                      <td>{{ item.trip_id || "null" }}</td>
                      <td>{{ item.date_time }}</td>
                    </tr>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { mdiMapSearch } from "@mdi/js"
import axios from "axios"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"

import store from "@/store"
import VehicleGPSHistoryMap from "./VehicleGPSHistoryMap.vue"
import urlHelpers from "@/mixins/urlHelpers"
import globalHelpers from "@/mixins/globalHelpers"

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  name: "DateRangePicker",
  components: {
    VehicleGPSHistoryMap,
  },
  mixins: [urlHelpers, globalHelpers],
  props: {
    vehicle: {
      type: Object,
      required: true,
    },
  },
  setup() {
    return {
      icons: {
        mdiMapSearch,
      },
    }
  },
  data() {
    return {
      currentTab: "",
      gpsProgress: {},
      eventsProgress: {},
      tripReportsProgress: {},
      dateTimes: {},
      gpsData: [],
      eventsData: [],
      dataByEventId: [],
      tripReports: [],
      vehicleId: this.vehicle.vehicle_id,
      request: axios.CancelToken.source(),
      eventsRequest: axios.CancelToken.source(),
      tripReportsRequest: axios.CancelToken.source(),
      gpsStartDateTime: dayjs.tz(new Date(), "Pacific/Auckland").hour("6").format("YYYY-MM-DDT06:00"),
      gpsEndDateTime: dayjs.tz(new Date(), "Pacific/Auckland").format("YYYY-MM-DDTHH:mm"),
      tripReportsStartDate: dayjs.tz(new Date(), "Pacific/Auckland").subtract(1, "day").format("YYYY-MM-DD"),
      tripReportsEndDate: dayjs.tz(new Date(), "Pacific/Auckland").subtract(1, "day").format("YYYY-MM-DD"),
      eventsStartDate: dayjs.tz(new Date(), "Pacific/Auckland").subtract(1, "day").format("YYYY-MM-DD"),
      eventsEndDate: dayjs.tz(new Date(), "Pacific/Auckland").subtract(1, "day").format("YYYY-MM-DD"),
    }
  },
  mounted() {
    window.addEventListener("routeChanged", this.handleRouteChange)
  },
  methods: {
    formattedDate(date) {
      return date.toISOString().split(".")[0]
    },
    utcDate(date) {
      return new Date(Date.UTC(new Date(date).getFullYear(), new Date(date).getMonth(), new Date(date).getDate()))
    },
    hoursFromNow(hours, date = new Date()) {
      return new Date(
        Date.UTC(
          new Date(date).getFullYear(),
          new Date(date).getMonth(),
          new Date(date).getDate(),
          new Date(date).getHours() + hours,
          new Date(date).getMinutes(),
        ),
      )
    },

    today() {
      return dayjs.tz(new Date(), "Pacific/Auckland").format("YYYY-MM-DDTHH:mm")
    },
    yesterday() {
      return dayjs.tz(new Date(), "Pacific/Auckland").subtract(1, "day").format("YYYY-MM-DD")
    },
    lastMonth() {
      return dayjs.tz(new Date(), "Pacific/Auckland").subtract(30, "day").format("YYYY-MM-DD")
    },

    handleRouteChange() {
      this.request.cancel("Navigated away, cancelling request")
      this.eventsRequest.cancel("Navigated away, cancelling request")
    },

    padZero(num) {
      return num < 10 ? `0${num}` : num
    },

    getHourlyIntervals(startTime, endTime) {
      const intervals = []
      const start = new Date(startTime)
      const end = new Date(endTime)

      // Loop through each hour between start and end time
      const current = new Date(start)
      while (current <= end) {
        const fixedDate = this.utcDate(new Date(current))
        fixedDate.setUTCHours(current.getHours())
        fixedDate.setUTCMinutes(current.getMinutes())
        intervals.push(this.formattedDate(fixedDate)) // Store a new Date object for each interval
        current.setHours(current.getHours() + 1)
      }

      return intervals
    },

    async fetchGPSData() {
      this.gpsData = []

      this.gpsData = await this.fetchPositionsForSelectedDates()

      this.addToSearchHistory(this.startDateTime, "startDateTime")
      this.addToSearchHistory(this.endDateTime, "endDateTime")
    },

    async fetchTripReportsData() {
      this.tripReports = []

      this.tripReports = await this.fetchTripReportsForSelectedDates()
    },

    async fetchEventsData() {
      this.eventsData = []

      this.eventsData = await this.fetchEventsForSelectedDates()
    },

    async showTripReportByEvent(eventId = null) {
      const cancelToken = this.eventsRequest.token
      const data = await store.dispatch(
        "getDeviceEventByEventId",
        {
          vehicle_id: this.vehicleId,
          event_type: "trip_reports",
          event_id: eventId,
        },
        cancelToken,
      )

      console.log(data)

      const updatedData = data?.map(item => {
        const stopAnnounceCounts = {}

        // First pass: Count stop_announced occurrences by stop_id
        item.event_data.logs.forEach(log => {
          if (log.log_type === "stop_announced" && log.stop_id) {
            stopAnnounceCounts[log.stop_id] = (stopAnnounceCounts[log.stop_id] || 0) + 1
          }
        })

        // Second pass: Map logs with timestamp formatting and count addition
        const logs = item.event_data.logs.map(log => {
          const formattedLog = {
            ...log,
            timestamp: dayjs.unix(log.timestamp).tz("Pacific/Auckland").format("YYYY-MM-DD HH:mm:ss"),
          }

          if (log.log_type === "stop_announced" && log.stop_id) {
            formattedLog.stop_announcement_count = stopAnnounceCounts[log.stop_id]
          }

          return formattedLog
        })

        return {
          ...item,
          event_data: {
            ...item.event_data,
            logs,
            start_datetime: dayjs
              .unix(item.event_data.start_timestamp)
              .tz("Pacific/Auckland")
              .format("YYYY-MM-DD HH:mm"),
            end_datetime: dayjs.unix(item.event_data.end_timestamp).tz("Pacific/Auckland").format("YYYY-MM-DD HH:mm"),
          },
        }
      })

      if (updatedData.some(item => item.error)) {
        throw new Error("An error occurred in the data")
      }

      this.dataByEventId = updatedData

      // Load in the GPS positions for this trip
      this.gpsStartDateTime = updatedData[0].event_data.start_datetime
      this.gpsEndDateTime = updatedData[0].event_data.end_datetime

      this.gpsData = await this.fetchPositionsForSelectedDates()
    },

    async fetchPositionsForSelectedDates() {
      this.request.cancel("New fetch requested")
      this.request = axios.CancelToken.source()

      const currentRequest = this.request
      const dateTimes = this.getHourlyIntervals(this.gpsStartDateTime, this.gpsEndDateTime)
      const total = dateTimes.length

      let combinedData = []

      this.gpsProgress = {
        total,
        current: 1,
        percentage: 0,
        finished: false,
      }

      for (let i = 0; i < total; i += 1) {
        if (this.request !== currentRequest) {
          console.log("Skipping...")
          // eslint-disable-next-line no-continue
          continue
        }

        this.dateTimes.gpsStartDateTime = dateTimes[i]
        this.dateTimes.gpsEndDateTime = dayjs
          .tz(this.dateTimes.gpsStartDate, "Pacific/Auckland")
          .add(1, "hour")
          .format("YYYY-MM-DDTHH:mm:ss")

        if (this.gpsEndDateTime < this.dateTimes.gpsEndDateTime) {
          this.dateTimes.gpsEndDateTime = dayjs
            .tz(this.gpsEndDateTime, "Pacific/Auckland")
            .format("YYYY-MM-DDTHH:mm:ss")
        }

        try {
          // eslint-disable-next-line no-await-in-loop
          const data = await store.dispatch("getPositions", {
            vehicle_id: this.vehicleId,
            start_datetime: this.dateTimes.gpsStartDateTime,
            end_datetime: this.dateTimes.gpsEndDateTime,
            cancelToken: this.request.token,
          })

          if (data.error) {
            throw new Error(data.error)
          }

          combinedData = [...combinedData, ...data]
        } catch (e) {
          console.error(e)
        }

        this.gpsProgress = {
          total,
          current: i + 1,
          percentage: ((i + 1) / total) * 100,
          finished: i + 1 === total,
        }
      }

      return combinedData
    },

    async fetchEventsForSelectedDates() {
      this.eventsRequest.cancel("New fetch requested")
      this.eventsRequest = axios.CancelToken.source()

      let combinedData = []

      this.eventsProgress = {
        total: 2,
        current: 0,
        percentage: 0,
        finished: false,
      }

      await new Promise(resolve => setTimeout(resolve, 2000))

      try {
        const data = await store.dispatch("getDeviceEventsByVehicleId", {
          vehicle_id: this.vehicleId,
          event_type: "trip_assignments",
          start_datetime: dayjs.tz(this.eventsStartDate, "Pacific/Auckland").format("YYYY-MM-DD 00:00:00"),
          end_datetime: dayjs.tz(this.eventsEndDate, "Pacific/Auckland").format("YYYY-MM-DD 23:59:59"),
          cancelToken: this.eventsRequest.token,
        })

        if (data.error) {
          throw new Error(data.error)
        }

        combinedData = [
          ...combinedData,
          ...data.map(i => ({
            type: i.event_type,
            ...i.event_data,
          })),
        ]
      } catch (e) {
        console.error(e)
      }

      this.eventsProgress = {
        total: 2,
        current: 1,
        percentage: 50,
        finished: false,
      }

      await new Promise(resolve => setTimeout(resolve, 2000))

      try {
        const data = await store.dispatch("getDeviceEventsByVehicleId", {
          vehicle_id: this.vehicleId,
          event_type: "off_route_messages",
          start_datetime: dayjs.tz(this.eventsStartDate, "Pacific/Auckland").format("YYYY-MM-DD 00:00:00"),
          end_datetime: dayjs.tz(this.eventsEndDate, "Pacific/Auckland").format("YYYY-MM-DD 23:59:59"),
          cancelToken: this.eventsRequest.token,
        })

        if (data.error) {
          throw new Error(data.error)
        }

        combinedData = [
          ...combinedData,
          ...data.map(i => ({
            type: i.event_type,
            ...i.event_data,
          })),
        ]
      } catch (e) {
        console.error(e)
      }

      this.eventsProgress = {
        total: 2,
        current: 2,
        percentage: 100,
        finished: true,
      }

      return combinedData
    },

    async fetchTripReportsForSelectedDates() {
      this.tripReportsRequest.cancel("New fetch requested")
      this.tripReportsRequest = axios.CancelToken.source()

      let combinedData = []

      this.tripReportsProgress = {
        total: 1,
        current: 0,
        percentage: 0,
        finished: false,
      }

      try {
        const data = await store.dispatch("getDeviceEventsByVehicleId", {
          vehicle_id: this.vehicleId,
          event_type: "trip_reports",
          start_datetime: dayjs.tz(this.tripReportsStartDate, "Pacific/Auckland").format("YYYY-MM-DD 00:00:00"),
          end_datetime: dayjs.tz(this.tripReportsEndDate, "Pacific/Auckland").format("YYYY-MM-DD 23:59:59"),
          cancelToken: this.eventsRequest.token,
        })

        if (data.error) {
          throw new Error(data.error)
        }

        data.forEach(item => {
          item.event_data.start_datetime = dayjs
            .unix(item.event_data.start_timestamp)
            .tz("Pacific/Auckland")
            .format("YYYY-MM-DD HH:mm")
          item.event_data.end_datetime = dayjs
            .unix(item.event_data.end_timestamp)
            .tz("Pacific/Auckland")
            .format("YYYY-MM-DD HH:mm")
        })

        combinedData = [
          ...data.map(i => ({
            type: i.event_type,
            ...i.event_data,
          })),
        ]
      } catch (e) {
        console.error(e)
      }

      this.tripReportsProgress = {
        total: 1,
        current: 1,
        percentage: 100,
        finished: true,
      }

      return combinedData
    },

    formatPercentage(value) {
      const formatter = new Intl.NumberFormat("en-US", {
        style: "percent",
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      })

      return formatter.format(value)
    },

    getShortTripId(trip_id) {
      return trip_id.split("__").slice(0, 3).join("__")
    },
  },
}
</script>

<style lang="scss" scoped>
.progress {
  margin-bottom: 1rem;

  &__message {
    text-align: center;
    margin: 0.25rem 0;
  }
}

.log-type-color {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  margin-right: 0.5rem;
}
</style>
